import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import StepWizard from 'react-step-wizard'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'

import ortodontiaService from '../../../services/ortodontia.service'
import {
  statusAvaliacaoOrtodontica,
  obterExameRadiologicoPronto,
  obterIdAvaliacaoOrtodontia,
  isValidGuid,
} from '../../../constants/agendamento.constants'
import PermissoesConstants from '../../../constants/permissoes.constants'
import SystemConstants from '../../../constants/system.constants'
import MenuContext from '../../../contexts/MenuContexts'
import { formatMoney, formatFromMoney } from '../../../utils/string.utils'
import PageLoading from '../../Common/PageLoading'
import AgendamentoRadiologia from './AgendamentoRadiologia'
import SelecaoPlanoPagamento from './SelecaoPlanoPagamento'
import Impressao from './Impressao'

export default function AtendimentoRecepcao(props) {
  const { agendamento, history } = props
  const acoes = useContext(MenuContext) || []
  const acoesPermitidas = acoes.reduce((acc, a) => ({ ...acc, [a.nome]: a }), {})
  const permitirContratoManual = !!acoesPermitidas[PermissoesConstants.CONTRATO_MANUAL]

  const [isLoading, setIsLoading] = useState(true)
  const [wizardRef, setWizardRef] = useState(null)
  const [clinicasRadiologicas, setClinicasRadiologicas] = useState([])
  const [planosOrtodonticos, setPlanosOrtodonticos] = useState([])
  const [planoPagamento, setPlanoPagamento] = useState({})
  const [avaliacao, setAvaliacao] = useState(
    isValidGuid(agendamento?.avaliacaoOrtodontia?.id) ? agendamento.avaliacaoOrtodontia : null
  )
  const [pularParaPasso, setPularParaPasso] = useState(null)
  const [dataVencimento, setDataVencimento] = useState(new Date())
  const [pagamentoAparelho, setPagamentoAparelho] = useState(null)
  const [aparelhoOptions, setAparelhoOptions] = useState([])
  const [selectedAparelho, setSelectedAparelho] = useState(null)
  const [pagamentoEfetuado, setPagamentoEfetuado] = useState(null)
  const [tipoContrato, setTipoContrato] = useState(() => (permitirContratoManual ? 'manual' : 'digital'))
  const [modoFinalizacao, setModoFinalizacao] = useState(null)
  const [linkAssinatura, setLinkAssinatura] = useState(null)
  const [linksAssinatura, setLinksAssinatura] = useState(null)
  const [exameAnterior, setExameAnterior] = useState(null)

  useEffect(() => {
    if (!agendamento?.id) {
      history.push('/agendamento/ortodontia')
      return
    }
    didMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (wizardRef && pularParaPasso) {
      if (typeof wizardRef.goToStep === 'function') {
        wizardRef.goToStep(pularParaPasso)
      } else if (typeof wizardRef.nextStep === 'function') {
        wizardRef.nextStep()
      }
      setPularParaPasso(null)
    }
  }, [wizardRef, pularParaPasso])

  useEffect(() => {
    if (avaliacao && isValidGuid(avaliacao.aparelhoId)) {
      getAparelhoPaciente(avaliacao.aparelhoId)
    }
  }, [avaliacao])

  useEffect(() => {
    if (selectedAparelho && selectedAparelho.valorVenda > 0) {
      setPagamentoAparelho({
        valorTotal: selectedAparelho.valorVenda,
        dataVencimento: new Date(),
        numeroParcelas: 1,
        parcelas: [
          {
            valor: selectedAparelho.valorVenda,
            numero: 1,
            dataVencimento: new Date(),
            status: 0,
          },
        ],
      })
    } else {
      setPagamentoAparelho(null)
    }
  }, [selectedAparelho])

  const didMount = async () => {
    try {
      const avaliacaoId = obterIdAvaliacaoOrtodontia(agendamento)
      const [, , , avaliacaoData] = await Promise.all([
        getClinicas(),
        getPlanosOrtodonticos(),
        getAparelhos(),
        getAvaliacao(avaliacaoId),
      ])

      if (!avaliacaoId || !avaliacaoData) {
        toast.error('Não foi possível localizar a avaliação ortodôntica deste agendamento.')
        history.push('/ortodontia/avaliacao/dentista')
        return
      }

      const status = avaliacaoData?.statusAvaliacao
      const jaTemRadiologia = (avaliacaoData?.agendamentosRadiologia || []).length > 0

      if (
        status === statusAvaliacaoOrtodontica.TRATAMENTO_DEFINIDO.value ||
        status === statusAvaliacaoOrtodontica.AGUARDANDO_ASSINATURA.value
      ) {
        setPularParaPasso(2)
        return
      }

      if (status === statusAvaliacaoOrtodontica.EXAME_MARCADO.value || jaTemRadiologia) {
        toast.info(
          'O exame radiológico já foi lançado. O dentista precisa definir o tratamento para efetivar o novo contrato.'
        )
        history.push('/ortodontia/avaliacao/dentista')
        return
      }

      await carregarExameAnterior(avaliacaoData)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const carregarExameAnterior = async (avaliacaoAtual) => {
    try {
      if (isValidGuid(agendamento.dependenteId)) {
        const { data } = await ortodontiaService.getAvaliacoesDependente(agendamento.dependenteId)
        setExameAnterior(obterExameRadiologicoPronto(data, avaliacaoAtual?.id))
        return
      }
      const pacienteId = agendamento.pacienteId || avaliacaoAtual?.pacienteId
      if (!isValidGuid(pacienteId)) {
        setExameAnterior(null)
        return
      }
      const { data } = await ortodontiaService.getAvaliacoesPaciente(pacienteId)
      setExameAnterior(obterExameRadiologicoPronto(data, avaliacaoAtual?.id))
    } catch (error) {
      setExameAnterior(null)
    }
  }

  const getAparelhoPaciente = async (aparelhoId) => {
    if (!isValidGuid(aparelhoId)) return
    setIsLoading(true)
    try {
      const { data } = await ortodontiaService.getAparelhoById(aparelhoId)
      setSelectedAparelho({
        label: `${data.nome} - ${formatMoney(data.valorVenda)}`,
        value: data.id,
        valorVenda: data.valorVenda,
      })
      if (data.valorVenda > 0) {
        setPagamentoAparelho({
          valorTotal: data.valorVenda,
          dataVencimento: new Date(),
          numeroParcelas: 1,
          parcelas: [
            {
              valor: data.valorVenda,
              numero: 1,
              dataVencimento: new Date(),
              status: 0,
            },
          ],
        })
      } else {
        setPagamentoAparelho(null)
      }
    } catch (error) {}
    setIsLoading(false)
  }

  const getAparelhos = async () => {
    try {
      const { data } = await ortodontiaService.getAparelhosCadastrados()
      setAparelhoOptions(
        data.map((opt) => ({
          label: `${opt.nome} - ${formatMoney(opt.valorVenda)}`,
          value: opt.id,
          valorVenda: opt.valorVenda,
        }))
      )
    } catch (e) {
      setAparelhoOptions([])
    }
  }

  const getAvaliacao = async (id) => {
    if (!isValidGuid(id)) return null
    try {
      const { data } = await ortodontiaService.getAvaliacaoById(id)
      setAvaliacao(data)
      return data
    } catch (error) {
      console.error(error)
      return null
    }
  }

  const getClinicas = async () => {
    try {
      const { data } = await ortodontiaService.getClinicasCadastradas()
      setClinicasRadiologicas(data.filter((d) => d.ativo))
    } catch (error) {
      console.error(error)
    }
  }

  const getPlanosOrtodonticos = async () => {
    try {
      const { data } = await ortodontiaService.getPlanosCadastrados()
      setPlanosOrtodonticos(data.filter((d) => d.ativo))
    } catch (error) {
      console.error(error)
    }
  }

  const handlePlanoPagamento = async () => {
    const parcelamentoPlano = planoPagamento.id ? planoPagamento.prazo : planoPagamento.numeroParcelas
    let mensagemModal = `<ul class='text-left font-weight-normal'>
        <li class='text-break'>
          <strong>${planoPagamento.nome}</strong>:
          Total de <strong>${formatMoney(planoPagamento.valorTotal)}</strong>
          ${parcelamentoPlano > 1 ? `dividido em <strong>` + parcelamentoPlano + `x </strong>` : `à vista`}
        </li>`
    if (pagamentoAparelho) {
      const { numeroParcelas } = pagamentoAparelho
      mensagemModal += `<li class='text-break'>
          <strong>Aparelho</strong>:
          Total de <strong>${formatMoney(pagamentoAparelho.valorTotal)}</strong>
          ${numeroParcelas > 1 ? `dividido em <strong>` + numeroParcelas + `x </strong>` : `à vista`}
        </li>`
    }
    mensagemModal += `</ul>`
    const { value } = await Swal.fire({
      title: 'Confirmar a escolha do plano?',
      html: mensagemModal,
      type: 'question',
      showCancelButton: true,
      cancelButtonColor: '#dbdbdb',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Confirmar',
    })
    try {
      if (value) {
        setIsLoading(true)
        if (planoPagamento.id) {
          planoPagamento.numeroParcelas = planoPagamento.prazo || planoPagamento.numeroParcelas
          delete planoPagamento.id
          planoPagamento.dataVencimento = dataVencimento
        }
        const { valorFinalParcelas, diferenca } = getValorParcelas(0, 0)
        planoPagamento.parcelas = getParcelas(valorFinalParcelas, diferenca, 0)

        if (tipoContrato === 'manual' && permitirContratoManual) {
          const planoResponse = await ortodontiaService.gerarPlanoTratamentoOrtodontico(
            { ...avaliacao, agendamentoId: agendamento.id, aparelhoId: selectedAparelho?.value },
            planoPagamento,
            pagamentoAparelho
          )
          setPagamentoEfetuado({
            numeroPlano: planoResponse.data.numeroPlano,
            planoId: planoResponse.data.id,
            pagamentoAparelho,
            planoPagamento,
          })
          setModoFinalizacao('tradicional')
          wizardRef.nextStep()
        } else {
          const user = JSON.parse(localStorage.getItem(SystemConstants.getUser()) || '{}')
          const payload = {
            avaliacao: { ...avaliacao, agendamentoId: agendamento.id, aparelhoId: selectedAparelho?.value },
            pagamento: planoPagamento,
            pagamentoAparelho: pagamentoAparelho || undefined,
            responsavelEmpresaAssinaturaColaboradorId: user.colaboradorId || null,
          }
          const { data } = await ortodontiaService.gerarPlanoTratamentoOrtodonticoV2(payload)
          if (data?.sucesso && (data?.linkAssinatura || (data?.linksAssinatura?.length > 0))) {
            setLinkAssinatura((data.linkAssinatura || data.linksAssinatura?.[0]?.link) ?? null)
            setLinksAssinatura(data.linksAssinatura?.length > 0 ? data.linksAssinatura : null)
            setModoFinalizacao('digital')
            setPagamentoEfetuado({ planoPagamento, pagamentoAparelho })
            wizardRef.nextStep()
            toast.success('Link(s) de assinatura gerado(s).')
          } else {
            toast.error(data?.mensagemErro || 'Não foi possível gerar o link.')
          }
        }
      }
    } catch (error) {
      console.error(`ERROR`, error)
      toast.error(error?.response?.data?.mensagemErro || 'Erro ao processar.')
    }
    setIsLoading(false)
  }

  const numeroParcelasPlano = planoPagamento?.numeroParcelas ?? planoPagamento?.prazo

  function getValorParcelas(valorEntradaNumber, valorParcelas) {
    let diferenca = 0
    const totalParcelas = numeroParcelasPlano || 1
    valorParcelas = (planoPagamento.valorTotal - valorEntradaNumber) / totalParcelas
    diferenca = planoPagamento.valorTotal - valorEntradaNumber - valorParcelas.toFixed(2) * totalParcelas
    return { valorFinalParcelas: valorParcelas, diferenca }
  }

  function getParcelas(valorParcelas, diferencaPrimeiraParcela) {
    let parcelas = []
    const totalParcelas = numeroParcelasPlano || 1
    for (let index = 1; index < totalParcelas; index++) {
      const valor = index === 1 ? +(valorParcelas + diferencaPrimeiraParcela).toFixed(2) : +valorParcelas.toFixed(2)
      parcelas.push({
        valor: +valor.toFixed(2),
        numero: index + 1,
        dataVencimento: moment(dataVencimento)
          .add(index, 'month')
          .toISOString(),
      })
    }

    parcelas.unshift({
      valor: +valorParcelas.toFixed(2),
      numero: 1,
      dataVencimento: moment(dataVencimento).toISOString(),
    })
    return parcelas
  }

  const primeiraParcelaMaiorQueCinco = useMemo(() => {
    const numParcelas = planoPagamento?.numeroParcelas ?? planoPagamento?.prazo
    const valorTotal = Number(planoPagamento?.valorTotal)
    if (!numParcelas || numParcelas < 1 || !(valorTotal > 0)) return false
    try {
      const { valorFinalParcelas, diferenca } = getValorParcelas(0, 0)
      const parcelas = getParcelas(valorFinalParcelas, diferenca, 0)
      return (parcelas[0]?.valor ?? 0) > 5
    } catch {
      return false
    }
  }, [planoPagamento?.numeroParcelas, planoPagamento?.prazo, planoPagamento?.valorTotal, dataVencimento])

  const salvarAgendamento = async (data, clinica, formaPagamento) => {
    if (!clinica || !formaPagamento) {
      toast.error('Selecione a clínica radiológica e a forma de pagamento.')
      return
    }
    setIsLoading(true)
    try {
      const clinicaRadiologia = clinicasRadiologicas.find((c) => c.id === clinica.value)
      if (!clinicaRadiologia) {
        toast.error('Nenhuma clínica radiológica cadastrada ou selecionada.')
        setIsLoading(false)
        return
      }
      const novoAgendamento = {
        data: moment(data).format('YYYY-MM-DDTHH:mm:ss'),
        formaPagamento: formaPagamento.value,
        clinicaRadiologiaId: clinicaRadiologia.id,
      }
      await ortodontiaService.salvarAvaliacao({
        ...avaliacao,
        agendamentoId: agendamento.id,
        statusAvaliacao: statusAvaliacaoOrtodontica.EXAME_MARCADO.value,
        agendamentosRadiologia: [...(avaliacao.agendamentosRadiologia || []), novoAgendamento],
      })
      toast('Agendamento registrado com sucesso! O dentista pode definir o tratamento.', { type: toast.TYPE.SUCCESS })
      history.push('/agendamento/ortodontia')
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.[0]?.message || 'Não foi possível salvar o agendamento de radiologia.')
    }
    setIsLoading(false)
  }

  const utilizarExameAnterior = async () => {
    if (!exameAnterior || !avaliacao) return
    setIsLoading(true)
    try {
      await ortodontiaService.salvarAvaliacao({
        ...avaliacao,
        agendamentoId: agendamento.id,
        statusAvaliacao: statusAvaliacaoOrtodontica.EXAME_MARCADO.value,
        agendamentosRadiologia: [
          ...(avaliacao.agendamentosRadiologia || []),
          {
            data: exameAnterior.data,
            formaPagamento: exameAnterior.formaPagamento,
            clinicaRadiologiaId: exameAnterior.clinicaRadiologiaId,
            radiografiaPronta: true,
          },
        ],
      })
      toast.success('Exame anterior reaproveitado. O dentista pode definir o tratamento para efetivar o contrato.')
      history.push('/ortodontia/avaliacao/dentista')
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.[0]?.message || 'Não foi possível reaproveitar o exame anterior.')
    }
    setIsLoading(false)
  }

  const renderLoading = () => <PageLoading />

  return (
    <Fragment>
      <StepWizard isLazyMount instance={setWizardRef}>
        <AgendamentoRadiologia
          nomePaciente={
            agendamento && agendamento.dependente
              ? agendamento.dependente.nome
              : agendamento.paciente
              ? agendamento.paciente.nome
              : ''
          }
          clinicas={clinicasRadiologicas}
          salvarAgendamento={salvarAgendamento}
          exameAnterior={exameAnterior}
          utilizarExameAnterior={utilizarExameAnterior}
        />
        <SelecaoPlanoPagamento
          setPlanoPagamento={setPlanoPagamento}
          setIsLoading={setIsLoading}
          planosOrtodonticos={planosOrtodonticos}
          setPagamentoAparelho={setPagamentoAparelho}
          handleSalvar={handlePlanoPagamento}
          setDataVencimento={setDataVencimento}
          dataVencimento={dataVencimento}
          planoPagamento={planoPagamento}
          pagamentoAparelho={pagamentoAparelho}
          aparelhoOptions={aparelhoOptions}
          setSelectedAparelho={setSelectedAparelho}
          selectedAparelho={selectedAparelho}
          tipoContrato={tipoContrato}
          setTipoContrato={setTipoContrato}
          primeiraParcelaMaiorQueCinco={primeiraParcelaMaiorQueCinco}
          permitirContratoManual={permitirContratoManual}
        />
        <Impressao
          avaliacao={avaliacao}
          pagamentoEfetuado={pagamentoEfetuado}
          history={history}
          modoFinalizacao={modoFinalizacao}
          linkAssinatura={linkAssinatura}
          linksAssinatura={linksAssinatura}
          onReconsultarLink={() =>
            ortodontiaService.getContratosAssinatura('AvaliacaoOrtodontia', avaliacao?.id)
          }
        />
      </StepWizard>
      {isLoading && renderLoading()}
    </Fragment>
  )
}
